import EditIcon from "@mui/icons-material/Edit";
import { Box, Slider, Typography } from "@mui/material";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import {
  getNextAvailableColor,
  MAX_REGIONS,
  MIN_REGION_SIZE,
  ZOOM_MAX,
  ZOOM_MIN,
  ZOOM_TO_CURSOR,
  ZOOM_WHEEL_STEP,
} from "../regionColors";
import type { Region } from "../types";
import { DraggableRegion } from "./DraggableRegion";

function rectsOverlap(
  a: { x: number; y: number; width: number; height: number },
  b: { x: number; y: number; width: number; height: number },
): boolean {
  return (
    a.x < b.x + b.width &&
    a.x + a.width > b.x &&
    a.y < b.y + b.height &&
    a.y + a.height > b.y
  );
}

interface ImageCanvasProps {
  imageSrc: string;
  regions: Region[];
  onUpdateRegion: (id: string, partial: Partial<Region>) => void;
  onRemoveRegion: (id: string) => void;
  onAddRegion?: (region: Region) => void;
}

export const ImageCanvas = ({
  imageSrc,
  regions,
  onUpdateRegion,
  onRemoveRegion,
  onAddRegion,
}: ImageCanvasProps) => {
  const { t } = useTranslation();
  const overlayRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const panState = useRef<{
    startX: number;
    startY: number;
    scrollLeft: number;
    scrollTop: number;
  } | null>(null);
  // Set on Ctrl/Cmd + wheel so the layout effect can keep the point under the
  // cursor fixed across the zoom (zoom-to-cursor). Null for slider zoom, which
  // stays centered.
  const pendingZoom = useRef<{
    prevZoom: number;
    contentX: number;
    contentY: number;
    cursorX: number;
    cursorY: number;
  } | null>(null);
  const [imageSize, setImageSize] = useState<{
    width: number;
    height: number;
  } | null>(null);
  const drawState = useRef<{ startX: number; startY: number } | null>(null);
  const [drawRect, setDrawRect] = useState<{
    x: number;
    y: number;
    width: number;
    height: number;
  } | null>(null);
  const [zoom, setZoom] = useState(ZOOM_MIN);
  const zoomRef = useRef(zoom);
  zoomRef.current = zoom;
  const [isPanning, setIsPanning] = useState(false);
  const [isCtrlDown, setIsCtrlDown] = useState(false);
  const [isWheelZooming, setIsWheelZooming] = useState(false);

  const canAddRegion = regions.length < MAX_REGIONS;
  const slotsRemaining = MAX_REGIONS - regions.length;
  const nextColor = getNextAvailableColor(regions.map((r) => r.label));
  const isDrawing = !!drawRect && drawRect.width > 0 && drawRect.height > 0;

  const handleImageLoad = useCallback(
    (e: React.SyntheticEvent<HTMLImageElement>) => {
      const img = e.currentTarget;
      const canvas = canvasRef.current;
      if (!canvas) return;

      const { naturalWidth, naturalHeight } = img;
      const maxW = canvas.clientWidth;
      const maxH = canvas.clientHeight;

      // Fit the full image inside the canvas (contain). This fitted size is
      // treated as 100% zoom, so the whole picture is visible on load even for
      // very high-resolution images.
      const fitScale = Math.min(maxW / naturalWidth, maxH / naturalHeight, 1);
      setImageSize({
        width: naturalWidth * fitScale,
        height: naturalHeight * fitScale,
      });
    },
    [],
  );

  const startPan = useCallback((e: React.PointerEvent) => {
    const sc = scrollRef.current;
    if (!sc) return;
    e.preventDefault();
    setIsPanning(true);
    panState.current = {
      startX: e.clientX,
      startY: e.clientY,
      scrollLeft: sc.scrollLeft,
      scrollTop: sc.scrollTop,
    };

    const move = (me: PointerEvent) => {
      const start = panState.current;
      const node = scrollRef.current;
      if (!start || !node) return;
      node.scrollLeft = start.scrollLeft - (me.clientX - start.startX);
      node.scrollTop = start.scrollTop - (me.clientY - start.startY);
    };
    const up = () => {
      panState.current = null;
      setIsPanning(false);
      document.removeEventListener("pointermove", move);
      document.removeEventListener("pointerup", up);
    };
    document.addEventListener("pointermove", move);
    document.addEventListener("pointerup", up);
  }, []);

  // Ctrl/Cmd + wheel zooms toward the cursor. The listener must be attached
  // natively with { passive: false } — React's onWheel is passive, so
  // preventDefault there is ignored and the browser would zoom the whole page.
  useEffect(() => {
    const sc = scrollRef.current;
    if (!sc) return;
    const onWheel = (e: WheelEvent) => {
      if (!e.ctrlKey && !e.metaKey) return;
      e.preventDefault();
      const prev = zoomRef.current;
      const next = Math.round(
        Math.min(
          ZOOM_MAX,
          Math.max(ZOOM_MIN, prev - e.deltaY * ZOOM_WHEEL_STEP),
        ),
      );
      if (next === prev) return;
      // Capture cursor position (relative to the scroll viewport) and the
      // content point under it, so the layout effect can re-anchor the scroll
      // after the new size is applied. Read scrollLeft now, before re-render.
      // When ZOOM_TO_CURSOR is off, leave pendingZoom null → centered zoom.
      if (ZOOM_TO_CURSOR) {
        const rect = sc.getBoundingClientRect();
        const cursorX = e.clientX - rect.left;
        const cursorY = e.clientY - rect.top;
        pendingZoom.current = {
          prevZoom: prev,
          contentX: sc.scrollLeft + cursorX,
          contentY: sc.scrollTop + cursorY,
          cursorX,
          cursorY,
        };
      }
      setIsWheelZooming(true);
      setZoom(next);
    };
    sc.addEventListener("wheel", onWheel, { passive: false });
    return () => sc.removeEventListener("wheel", onWheel);
  }, []);

  // After a wheel zoom re-renders the wrapper to its new size, adjust the
  // scroll offset so the point that was under the cursor stays under it.
  // useLayoutEffect runs after DOM size update but before paint → no flicker.
  useLayoutEffect(() => {
    const sc = scrollRef.current;
    const pending = pendingZoom.current;
    pendingZoom.current = null;

    if (sc && pending) {
      // Use the real applied zoom ratio (zoom may have been clamped at MIN/MAX).
      const ratio = zoom / pending.prevZoom;
      sc.scrollLeft = pending.contentX * ratio - pending.cursorX;
      sc.scrollTop = pending.contentY * ratio - pending.cursorY;
    }
    setIsWheelZooming(false);
  }, [zoom]);

  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === "Control" || e.key === "Meta") setIsCtrlDown(true);
    };
    const up = (e: KeyboardEvent) => {
      if (e.key === "Control" || e.key === "Meta") setIsCtrlDown(false);
    };
    window.addEventListener("keydown", down);
    window.addEventListener("keyup", up);
    return () => {
      window.removeEventListener("keydown", down);
      window.removeEventListener("keyup", up);
    };
  }, []);

  const getPercentageFromEvent = useCallback(
    (e: React.PointerEvent | PointerEvent) => {
      const container = overlayRef.current;
      if (!container) return { px: 0, py: 0 };
      const rect = container.getBoundingClientRect();
      return {
        px: Math.max(
          0,
          Math.min(100, ((e.clientX - rect.left) / rect.width) * 100),
        ),
        py: Math.max(
          0,
          Math.min(100, ((e.clientY - rect.top) / rect.height) * 100),
        ),
      };
    },
    [],
  );

  const handleOverlayPointerDown = useCallback(
    (e: React.PointerEvent) => {
      if (e.ctrlKey || e.metaKey || e.button === 1) {
        startPan(e);
        return;
      }
      if (!canAddRegion || !onAddRegion) return;
      if ((e.target as HTMLElement).closest("[data-region]")) return;

      const { px, py } = getPercentageFromEvent(e);
      drawState.current = { startX: px, startY: py };
      setDrawRect({ x: px, y: py, width: 0, height: 0 });

      let lastPx = px;
      let lastPy = py;

      const handleMove = (me: PointerEvent) => {
        if (!drawState.current) return;
        const container = overlayRef.current;
        if (!container) return;
        const rect = container.getBoundingClientRect();
        const mx = Math.max(
          0,
          Math.min(100, ((me.clientX - rect.left) / rect.width) * 100),
        );
        const my = Math.max(
          0,
          Math.min(100, ((me.clientY - rect.top) / rect.height) * 100),
        );
        lastPx = mx;
        lastPy = my;

        const x = Math.min(drawState.current.startX, mx);
        const y = Math.min(drawState.current.startY, my);
        const width = Math.abs(mx - drawState.current.startX);
        const height = Math.abs(my - drawState.current.startY);
        setDrawRect({ x, y, width, height });
      };

      const handleUp = () => {
        document.removeEventListener("pointermove", handleMove);
        document.removeEventListener("pointerup", handleUp);

        const current = drawState.current;
        drawState.current = null;
        setDrawRect(null);

        if (!current) return;

        const finalX = Math.min(current.startX, lastPx);
        const finalY = Math.min(current.startY, lastPy);
        const finalW = Math.abs(lastPx - current.startX);
        const finalH = Math.abs(lastPy - current.startY);

        if (finalW < MIN_REGION_SIZE || finalH < MIN_REGION_SIZE) return;

        const proposed = {
          x: finalX,
          y: finalY,
          width: finalW,
          height: finalH,
        };
        const overlaps = regions.some((r) => rectsOverlap(proposed, r));
        if (overlaps) return;

        const { color, label } = getNextAvailableColor(
          regions.map((r) => r.label),
        );
        onAddRegion({
          id: `${label}-${crypto.randomUUID().slice(0, 8)}`,
          ...proposed,
          color,
          label,
        });
      };

      document.addEventListener("pointermove", handleMove);
      document.addEventListener("pointerup", handleUp);
    },
    [canAddRegion, onAddRegion, getPercentageFromEvent, regions, startPan],
  );

  return (
    <Box
      ref={canvasRef}
      sx={{
        position: "relative",
        width: "100%",
        height: "clamp(320px, calc(100vh - 360px), 620px)",
        overflow: "hidden",
        backgroundColor: "#111",
        borderRadius: "var(--card-small, 8px)",
      }}
    >
      <Box
        ref={scrollRef}
        sx={{
          position: "absolute",
          inset: 0,
          display: "flex",
          overflow: zoom > ZOOM_MIN ? "auto" : "hidden",
          cursor: isPanning ? "grabbing" : isCtrlDown ? "grab" : "default",
        }}
      >
        <Box
          sx={{
            position: "relative",
            margin: "auto",
            flexShrink: 0,
            width: imageSize ? imageSize.width * (zoom / 100) : "100%",
            height: imageSize ? imageSize.height * (zoom / 100) : "100%",
            transition: isWheelZooming
              ? "none"
              : "width 0.1s ease-out, height 0.1s ease-out",
          }}
        >
          <img
            src={imageSrc}
            alt="Uploaded for region selection"
            onLoad={handleImageLoad}
            style={{
              width: "100%",
              height: "100%",
              objectFit: "contain",
              display: "block",
              userSelect: "none",
              pointerEvents: "none",
              visibility: imageSize ? "visible" : "hidden",
            }}
            draggable={false}
          />

          {imageSize && (
            <Box
              ref={overlayRef}
              data-overlay
              onPointerDown={handleOverlayPointerDown}
              sx={{
                position: "absolute",
                inset: 0,
                backgroundColor: "rgba(0,0,0,0.35)",
                cursor: isPanning
                  ? "grabbing"
                  : isCtrlDown
                    ? "grab"
                    : canAddRegion
                      ? "crosshair"
                      : "default",
              }}
            >
              {regions.map((region) => (
                <DraggableRegion
                  key={region.id}
                  region={region}
                  otherRegions={regions.filter((r) => r.id !== region.id)}
                  containerRef={overlayRef}
                  onUpdate={onUpdateRegion}
                  onRemove={onRemoveRegion}
                  canRemove={regions.length > 1}
                  isPanMode={isCtrlDown}
                  isPanning={isPanning}
                />
              ))}
              {isDrawing && drawRect && (
                <Box
                  sx={{
                    position: "absolute",
                    left: `${drawRect.x}%`,
                    top: `${drawRect.y}%`,
                    width: `${drawRect.width}%`,
                    height: `${drawRect.height}%`,
                    border: `2px dashed ${nextColor.color}`,
                    backgroundColor: "rgba(255,255,255,0.06)",
                    pointerEvents: "none",
                  }}
                />
              )}
            </Box>
          )}
        </Box>
      </Box>

      <Box
        sx={{
          position: "absolute",
          top: "var(--spacing12, 12px)",
          left: "50%",
          transform: "translateX(-50%)",
          display: "flex",
          alignItems: "center",
          gap: "var(--spacing8, 8px)",
          backgroundColor: isDrawing
            ? `${nextColor.color}cc`
            : "rgba(26,26,26,0.75)",
          borderRadius: "var(--card-large, 16px)",
          padding: "var(--spacing8, 8px) var(--spacing16, 16px)",
          pointerEvents: "none",
          transition: "background-color 0.2s",
        }}
      >
        {isDrawing ? (
          <>
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: "50%",
                backgroundColor: "var(--global-neutral-white, #fff)",
                flexShrink: 0,
              }}
            />
            <Typography
              sx={{
                fontSize: "var(--body-b3-12-regular-size, 12px)",
                color: "var(--global-neutral-white, #fff)",
                whiteSpace: "nowrap",
              }}
            >
              {t("ldp_explore_image_region_release_hint", {
                label: nextColor.label,
              })}
            </Typography>
          </>
        ) : (
          <>
            <EditIcon
              sx={{
                fontSize: 16,
                color: "var(--global-neutral-white, #fff)",
              }}
            />
            <Typography
              sx={{
                fontSize: "var(--body-b3-12-regular-size, 12px)",
                color: "var(--global-neutral-white, #fff)",
                whiteSpace: "nowrap",
              }}
            >
              {canAddRegion
                ? t("ldp_explore_image_region_draw_hint")
                : t("ldp_explore_image_region_max_reached")}
            </Typography>
            {canAddRegion && (
              <Typography
                sx={{
                  fontSize: "var(--body-b3-12-regular-size, 12px)",
                  color: "rgba(255,255,255,0.6)",
                  whiteSpace: "nowrap",
                }}
              >
                ·{" "}
                {t("ldp_explore_image_region_slots_remaining", {
                  count: slotsRemaining,
                })}
              </Typography>
            )}
          </>
        )}
      </Box>

      <Box
        sx={{
          position: "absolute",
          bottom: "var(--spacing12, 12px)",
          left: "50%",
          transform: "translateX(-50%)",
          display: "flex",
          alignItems: "center",
          gap: "var(--spacing12, 12px)",
          width: "min(320px, 80%)",
          backgroundColor: "rgba(26,26,26,0.75)",
          borderRadius: "var(--card-large, 16px)",
          padding: "var(--spacing8, 8px) var(--spacing16, 16px)",
        }}
      >
        <Slider
          value={zoom}
          min={ZOOM_MIN}
          max={ZOOM_MAX}
          onChange={(_, value) => setZoom(Math.round(value as number))}
          aria-label={t("ldp_explore_image_region_zoom_aria")}
          sx={{
            color: "var(--global-neutral-white, #fff)",
            "& .MuiSlider-rail": { opacity: 0.4 },
          }}
        />
        <Typography
          sx={{
            fontSize: "var(--body-b3-12-regular-size, 12px)",
            color: "var(--global-neutral-white, #fff)",
            whiteSpace: "nowrap",
            minWidth: "36px",
            textAlign: "right",
          }}
        >
          {zoom}%
        </Typography>
      </Box>
    </Box>
  );
};
